import { getInputFormat, ReadFileSystem } from '@playcanvas/splat-transform';
import { AppBase, Asset, GSplatResource, Vec3 } from 'playcanvas';

import { Events } from './events';
import { loadGSplatData, validateGSplatData } from './io';
import { Model } from './model';
import { Splat } from './splat';

const getOrientation = (filename: string) => {
    switch (getInputFormat(filename)) {
        case 'spz':
            return new Vec3(0, 0, 0);
        case 'lcc':
            return new Vec3(90, 0, 180);
        default:
            return new Vec3(0, 0, 180);
    }
};

// handles loading gsplat assets using splat-transform
class AssetLoader {
    app: AppBase;
    events: Events;

    constructor(app: AppBase, events: Events) {
        this.app = app;
        this.events = events;
    }

    async load(filename: string, fileSystem: ReadFileSystem, animationFrame?: boolean, skipReorder?: boolean) {
        if (!animationFrame) {
            this.events.fire('startSpinner');
        }

        try {
            // Skip reordering for animation frames (speed) or when explicitly requested (already ordered)
            const gsplatData = await loadGSplatData(filename, fileSystem, skipReorder || animationFrame);
            validateGSplatData(gsplatData);

            const asset = new Asset(filename, 'gsplat', { url: `local-asset-${Date.now()}`, filename });
            this.app.assets.add(asset);
            asset.resource = new GSplatResource(this.app.graphicsDevice, gsplatData);

            return new Splat(asset, getOrientation(filename));
        } finally {
            if (!animationFrame) {
                this.events.fire('stopSpinner');
            }
        }
    }

    // load a traditional polygonal model (glB/glTF) as a container asset
    async loadModel(file: File, filename: string) {
        this.events.fire('startSpinner');

        // retain the source bytes so the model can be re-saved into a .ssproj document
        const buffer = await file.arrayBuffer();
        const url = URL.createObjectURL(new Blob([buffer]));
        try {
            const asset = new Asset(filename, 'container', { url, filename });
            this.app.assets.add(asset);

            await new Promise<void>((resolve, reject) => {
                asset.once('load', () => resolve());
                asset.once('error', err => reject(new Error(err)));
                this.app.assets.load(asset);
            });

            const model = new Model(asset, filename);
            model.fileData = new Uint8Array(buffer);
            return model;
        } finally {
            URL.revokeObjectURL(url);
            this.events.fire('stopSpinner');
        }
    }
}

export { AssetLoader };
